// Localização: frontend/src/pages/Management/MedicamentoPage.js (Versão 0.2.0 - SearchContext)

import React, { useState, useEffect, useMemo } from 'react';
import PageTitle from '../../components/Layout/PageTitle';
import Modal from '../../components/UI/Modal';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSearch } from '../../context/SearchContext';
import { useNotification } from '../../context/NotificationContext';

// 🎯 IMPORTANDO OS HOOKS ESPECÍFICOS:
import { useMedicamentos } from '../../hooks/useMedicamentos';
import { useLotes } from '../../hooks/useLotes';

// Componentes Desmembrados
import MedicamentoForm from '../../components/Management/MedicamentoForm';
import LoteForm from '../../components/Management/LoteForm';
import MedicamentoList from '../../components/Management/MedicamentoList';
import LoteList from '../../components/Management/LoteList';
import AlertaList from '../../components/Management/AlertaList';
import PrimaryButton from '../../components/Common/PrimaryButton';


const MedicamentoPage = () => {
    const { isAuthenticated } = useAuth();
    const { checkPermission } = usePermissions();
    const { success: showSuccess, error: showError } = useNotification();
    
    // --- ESTADOS DE UI E CONTROLE (Locais) ---
    const [activeTab, setActiveTab] = useState('medicamentos'); 
    
    // 🎯 USANDO SEARCHCONTEXT: Substituir estado local por contexto global
    const { searchTerm, setSearchPlaceholder } = useSearch();
    
    // Estados de Modal e Edição
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMedicamento, setEditingMedicamento] = useState(null);
    const [isLoteModalOpen, setIsLoteModalOpen] = useState(false);
    
    // Gatilho de Refresh Local (Mantido para forçar o useEffect)
    const [pageRefreshTrigger, setPageRefreshTrigger] = useState(0); 
    const triggerRefresh = () => setPageRefreshTrigger(prev => prev + 1);


    // --- CONSUMO DO HOOKS ---
    const { 
        medicamentos, 
        isLoadingMedicamentos, 
        fetchMedicamentos, 
        createMedicamento, 
        updateMedicamento, 
        deleteMedicamento: deleteMedHook,
    } = useMedicamentos();

    const {
        lotes,
        alertas,
        isLoadingLotes,
        isLoadingAlertas,
        fetchLotes,
        fetchAlertas,
        createLote,
        cancelarLote,
    } = useLotes();
    
    // --- PERMISSÕES ---
    const canCreateMed = checkPermission('medicamentos', 'can_create');
    const canEditMed = checkPermission('medicamentos', 'can_edit');
    const canDeleteMed = checkPermission('medicamentos', 'can_delete');
    const canAccess = checkPermission('medicamentos', 'can_access') || checkPermission('estoque', 'can_read'); 
    const canCreateLote = checkPermission('estoque', 'can_create');


    // -------------------------------------------------------------------
    // EFEITO COLATERAL: Busca de dados
    // -------------------------------------------------------------------
    useEffect(() => {
        if (!isAuthenticated || !canAccess) return;
        
        fetchMedicamentos();

        if (activeTab === 'lotes') {
            fetchLotes();
        } else if (activeTab === 'alertas') {
            fetchAlertas();
        }
        
    }, [
        isAuthenticated, 
        canAccess, 
        activeTab, 
        pageRefreshTrigger,
        fetchMedicamentos, 
        fetchLotes,
        fetchAlertas
    ]);

    // 🎯 Configurar o placeholder de busca para esta página baseado na tab ativa
    useEffect(() => {
        if (activeTab === 'medicamentos') {
            setSearchPlaceholder('Buscar por nome, dose ou instruções...');
        } else if (activeTab === 'lotes') {
            setSearchPlaceholder('Buscar por medicamento, lote ou validade...');
        } else {
            // Para a tab de alertas, limpa o placeholder (busca desabilitada no header)
            setSearchPlaceholder('');
        }
    }, [activeTab, setSearchPlaceholder]);


    // -------------------------------------------------------------------
    // LÓGICA DE FILTRAGEM (MEMOIZADA)
    // -------------------------------------------------------------------
    const filteredMedicamentos = useMemo(() => {
        if (!searchTerm) return medicamentos; 
        const lowerCaseSearch = searchTerm.toLowerCase();

        return medicamentos.filter(med => (
            med.nome_medicamento.toLowerCase().includes(lowerCaseSearch) ||
            med.dose.toLowerCase().includes(lowerCaseSearch) ||
            med.unidade_medida.toLowerCase().includes(lowerCaseSearch) ||
            med.instrucoes?.toLowerCase().includes(lowerCaseSearch)
        ));
    }, [medicamentos, searchTerm]);

    // 🎯 Filtro de lotes usando SearchContext
    const filteredLotes = useMemo(() => {
        if (!searchTerm) return lotes;
        const lowerCaseSearch = searchTerm.toLowerCase();

        return lotes.filter(lote => (
            lote.nome_medicamento?.toLowerCase().includes(lowerCaseSearch) ||
            lote.lote?.toLowerCase().includes(lowerCaseSearch) ||
            lote.unidade_medida?.toLowerCase().includes(lowerCaseSearch) ||
            new Date(lote.data_validade).toLocaleDateString('pt-BR').includes(lowerCaseSearch)
        ));
    }, [lotes, searchTerm]);


    // -------------------------------------------------------------------
    // HANDLERS DE AÇÃO (CRUD)
    // -------------------------------------------------------------------
    const handleSaveMedicamento = async (formData) => {
        try {
            if (editingMedicamento) {
                await updateMedicamento(editingMedicamento.id, formData);
                showSuccess('Medicamento atualizado com sucesso!');
            } else {
                await createMedicamento(formData);
                showSuccess('Medicamento cadastrado com sucesso!');
            }
            setIsModalOpen(false);
            triggerRefresh();
        } catch (error) {
            console.error("Erro ao salvar medicamento:", error.message);
            showError(`Erro ao salvar: ${error.message}`);
        }
    };
    
    const handleDeleteMedicamento = async (id) => {
        if (!window.confirm("Tem certeza que deseja excluir este medicamento? Esta ação não pode ser desfeita.")) return;
        try {
            await deleteMedHook(id);
            showSuccess('Medicamento excluído com sucesso!');
            triggerRefresh();
        } catch (error) {
            console.error("Erro ao deletar medicamento:", error.message);
            showError(`Erro ao deletar: ${error.message}`);
        }
    };
    
    const handleSaveLote = async (formData) => {
        try {
            await createLote(formData); 
            showSuccess('Lote cadastrado com sucesso!');
            setIsLoteModalOpen(false);
            triggerRefresh();
        } catch (error) {
            console.error("Erro ao cadastrar lote:", error.message);
            showError(`Erro ao cadastrar lote: ${error.message}`);
        }
    };

    const handleCancelarLote = async (id) => {
        try {
            await cancelarLote(id);
            showSuccess('Lote cancelado com sucesso!');
            triggerRefresh();
        } catch (error) {
            console.error("Erro ao cancelar lote:", error.message);
            showError(`Erro ao cancelar lote: ${error.message}`);
        }
    };

    const handleOpenCreateModal = () => {
        setEditingMedicamento(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (medicamento) => {
        setEditingMedicamento(medicamento);
        setIsModalOpen(true);
    };


    if (!canAccess) {
        return (
            <> 
                <PageTitle 
                    title="Gestão de Medicamentos"
                    subtitle="Você não tem permissão para acessar o módulo de medicamentos"
                />
                <p className="text-red-500">Você não tem permissão para acessar o módulo de medicamentos.</p>
            </>
        );
    }
    
    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
                    <PageTitle 
                        title="Gestão de Medicamentos e Estoque"
                        subtitle="Gerencie medicamentos, lotes e controle de validade"
                    />
                    
                    {/* Botão Cadastrar Novo Lote - Visível apenas na aba de lotes */}
                    {activeTab === 'lotes' && canCreateLote && (
                        <PrimaryButton 
                            onClick={() => setIsLoteModalOpen(true)}
                            disabled={medicamentos.length === 0} 
                            className={`${medicamentos.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <span className="hidden sm:inline">+ Cadastrar Novo Lote</span>
                            <span className="sm:hidden">+ Novo Lote</span>
                        </PrimaryButton>
                    )}
                </div>
                
                {/* 🎯 REFACTOR NA NAVEGAÇÃO: Adicionando flex-wrap para quebrar em telas pequenas */}
                <nav className="-mb-px flex flex-wrap gap-2 sm:gap-4 sm:space-x-4 sm:space-x-8 border-b border-gray-200" aria-label="Tabs">
                    <button 
                        onClick={() => setActiveTab('medicamentos')}
                        className={`py-2 sm:py-4 px-2 sm:px-1 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'medicamentos' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        <span className="hidden sm:inline">Cadastro de Medicamentos</span>
                        <span className="sm:hidden">Medicamentos</span>
                    </button>
                    <button 
                        onClick={() => setActiveTab('lotes')}
                        className={`py-2 sm:py-4 px-2 sm:px-1 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'lotes' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        <span className="hidden sm:inline">Controle de Lotes (Estoque)</span>
                        <span className="sm:hidden">Lotes</span>
                    </button>
                    <button 
                        onClick={() => setActiveTab('alertas')}
                        className={`py-2 sm:py-4 px-2 sm:px-1 text-xs sm:text-sm font-medium border-b-2 whitespace-nowrap ${activeTab === 'alertas' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                    >
                        <span className="hidden sm:inline">Alertas de Validade/Estoque</span>
                        <span className="sm:hidden">Alertas</span>
                    </button>
                </nav>

                {/* Conteúdo das Abas */}
                <div className="pt-6">
                    {activeTab === 'medicamentos' && (
                        <MedicamentoList
                            data={filteredMedicamentos}
                            isLoading={isLoadingMedicamentos}
                            canCreate={canCreateMed}
                            canEdit={canEditMed}
                            canDelete={canDeleteMed}
                            handleOpenCreateModal={handleOpenCreateModal}
                            handleOpenEditModal={handleOpenEditModal}
                            handleDeleteMedicamento={handleDeleteMedicamento}
                        />
                    )}
                    
                    {activeTab === 'lotes' && (
                        <LoteList
                            data={filteredLotes}
                            isLoading={isLoadingLotes}
                            onCancelarLote={handleCancelarLote}
                        />
                    )}
                    
                    {activeTab === 'alertas' && (
                        <AlertaList
                            data={alertas}
                            isLoading={isLoadingAlertas}
                        />
                    )}
                </div>
            </div> {/* Fim da div externa */}

            {/* Modais fora da div principal */}
            
            {/* Modal de Cadastro/Edição de Medicamento */}
            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingMedicamento ? 'Editar Medicamento' : 'Novo Medicamento'}>
                <MedicamentoForm 
                    initialData={editingMedicamento} 
                    onSubmit={handleSaveMedicamento} 
                    onClose={() => setIsModalOpen(false)} 
                    isEditing={!!editingMedicamento}
                />
            </Modal>
            
            {/* Modal de Cadastro de Lote */}
            <Modal isOpen={isLoteModalOpen} onClose={() => setIsLoteModalOpen(false)} title="Cadastrar Novo Lote de Medicamento">
                <LoteForm 
                    medicamentosList={medicamentos}
                    onSubmit={handleSaveLote} 
                    onClose={() => setIsLoteModalOpen(false)} 
                />
            </Modal>
        </div>
    );
};

export default MedicamentoPage;